<script setup lang="ts">
import { computed, ref } from 'vue';
import type { ListPlaybookEvaluationsResponse } from '@/api';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaPopover from '@/components/design/base/GaiaPopover.vue';
import GaiaColumnsMenu, {
  type GaiaColumnOption,
} from '@/components/design/composite/GaiaColumnsMenu.vue';
import { evaluationColumnOrder } from '@/composables/usePlaybookRunCells';

/**
 * Per-column visibility for an evaluation grid, shared by the review project's
 * analysis pane and the playbook runs view. Sits beside the type and group
 * filters rather than replacing them: those answer "show me this kind of
 * column", this one answers "not that one" — a playbook can carry thirty
 * evaluations and a reader usually wants six of them.
 */
const props = defineProps<{
  /** Every evaluation the playbook has, before any filter. */
  evaluations: ListPlaybookEvaluationsResponse;
  /** Unticked evaluation uids — a deny list, so new evaluations show by default. */
  hidden: string[];
}>();

const emit = defineEmits<{ 'update:hidden': [hidden: string[]] }>();

const query = ref('');

/** Ordered as the grid lays the columns out, then narrowed by the search. */
const options = computed<GaiaColumnOption[]>(() => {
  const needle = query.value.trim().toLowerCase();
  return evaluationColumnOrder(props.evaluations)
    .filter(evaluation => !needle || evaluation.name.toLowerCase().includes(needle))
    .map(evaluation => ({
      key: evaluation.uid,
      label: evaluation.name,
      visible: !props.hidden.includes(evaluation.uid),
    }));
});

/**
 * The tri-state row acts on what is listed, so with a search running it offers
 * the matches — and says so, since "All columns" would be a lie about scope.
 */
const selectAllLabel = computed(() =>
  query.value.trim()
    ? `All ${options.value.length} ${options.value.length === 1 ? 'match' : 'matches'}`
    : 'All columns'
);

/** How many of the playbook's columns the picker is currently holding back. */
const hiddenCount = computed(
  () => props.hidden.filter(uid => props.evaluations.some(item => item.uid === uid)).length
);

function toggle(uid: string): void {
  emit(
    'update:hidden',
    props.hidden.includes(uid) ? props.hidden.filter(item => item !== uid) : [...props.hidden, uid]
  );
}

function toggleAll(visible: boolean): void {
  const listed = new Set(options.value.map(option => option.key));
  emit(
    'update:hidden',
    visible
      ? props.hidden.filter(uid => !listed.has(uid))
      : [...new Set([...props.hidden, ...listed])]
  );
}
</script>

<template>
  <gaia-popover placement="bottom-end">
    <template #trigger>
      <gaia-button size="small" data-tour="eval.columns">
        Columns
        <template v-if="hiddenCount">· {{ hiddenCount }} hidden</template>
      </gaia-button>
    </template>
    <!-- GaiaColumnsMenu brings its own surface and caps its list at 264px with a
         scroller, so a thirty-evaluation playbook needs nothing extra here. -->
    <gaia-columns-menu
      v-model:search="query"
      searchable
      search-placeholder="Search evaluations…"
      :columns="options"
      :select-all-label="selectAllLabel"
      @toggle="toggle"
      @toggle-all="toggleAll"
    />
  </gaia-popover>
</template>
